// Static content data: services + cities. SEO-first, long-form.

export const BUSINESS = {
  name: "Maze Security Solutions",
  short: "Maze Security",
  phone: "(807) 907-2874",
  phoneHref: "tel:+18079072874",
  email: "info@mazesecurity.ca",
  emailHref: "mailto:info@mazesecurity.ca",
  address: "Provincial Operations Centre, Fredericton, NB, Canada",
  established: "2008",
  license: "NB Private Investigators & Security Services Act — Licensed & Insured",
};

export const SERVICES = [
  {
    slug: "uniformed-security-guards",
    name: "Uniformed Security Guards",
    tagline: "Visible deterrence. Trained personnel. 24/7 posts.",
    summary:
      "Provincially licensed uniformed guards for commercial, industrial, retail and residential sites across New Brunswick — day, night, weekend and holiday coverage.",
    long: [
      "Uniformed security guards remain the single most cost-effective deterrent against theft, vandalism, unauthorized access and workplace violence. Our uniformed officers hold Class 2 New Brunswick security licenses under the Private Investigators and Security Services Act, complete Use of Force training, First Aid / CPR certification, and pass a rigorous background screening before wearing our uniform.",
      "Every uniformed post in New Brunswick is briefed on site-specific post orders, emergency contact trees, evacuation procedures and local RCMP / municipal police liaison protocols. Officers file real-time digital patrol logs, incident reports and photographic evidence via our GuardTour dispatch platform, giving clients an auditable record of every hour billed.",
      "From front-of-house concierge in Saint John high-rises to overnight industrial gate control in Miramichi mills, uniformed New Brunswick Security Services personnel are the recognizable, professional presence that keeps your people, tenants and assets safe.",
    ],
    bullets: [
      "Class 2 licensed guards under NB Private Investigators & Security Services Act",
      "Use of Force, First Aid, CPR, WHMIS & Non-Violent Crisis Intervention certified",
      "GPS-tagged patrol tours & incident reporting portal",
      "Bilingual (English / French) officers available across all NB regions",
      "Short-notice deployment: as fast as 4 hours anywhere in New Brunswick",
    ],
    keywords: [
      "security guard services New Brunswick",
      "licensed security guards NB",
      "uniformed security guard Fredericton",
      "24 hour security guards Saint John",
      "hire security guard Moncton",
    ],
  },
  {
    slug: "mobile-patrol",
    name: "Mobile Patrol",
    tagline: "Marked vehicles. Random check-ins. Provincial coverage.",
    summary:
      "GPS-tracked marked patrol vehicles running randomized checks on your property — proven to reduce break-ins and vandalism by up to 68%.",
    long: [
      "Mobile patrol is the intelligent midpoint between a full-time guard post and no coverage at all. Our marked patrol trucks sweep client sites on randomized schedules — never the same time twice — so intruders cannot predict our arrival. Every stop is GPS-verified, time-stamped and delivered to you as a nightly PDF activity report.",
      "New Brunswick Security Services runs mobile patrol routes across every incorporated community we serve, from downtown Fredericton commercial strips to rural Charlotte County cottage properties. Vehicles carry radios, defibrillators, high-lumen flashlights and gate-lock verification tools. Officers physically dismount to test locks, walk perimeters and photograph anomalies — not just drive past.",
      "Mobile patrol is the highest-ROI service for construction contractors, property managers, dealerships, marinas and after-hours retail plazas throughout New Brunswick.",
    ],
    bullets: [
      "GPS-verified check-points with photo evidence",
      "Randomized routing prevents pattern-recognition by intruders",
      "Lock, gate & perimeter verification on foot",
      "Emergency alarm response bundled available",
      "Coverage from Woodstock to Caraquet, 365 nights a year",
    ],
    keywords: [
      "mobile patrol security New Brunswick",
      "mobile security services NB",
      "patrol company Fredericton",
      "vehicle patrol Moncton",
      "night patrol services Saint John",
    ],
  },
  {
    slug: "event-security",
    name: "Event Security",
    tagline: "Festivals, weddings, concerts, corporate events.",
    summary:
      "Certified event security for crowd management, access control, VIP escort and emergency response — Harvest Jazz to private galas.",
    long: [
      "New Brunswick hosts world-class events year-round: Harvest Jazz & Blues in Fredericton, the Area 506 Festival on the Saint John waterfront, Shediac Lobster Festival, Festival Acadien de Caraquet and hundreds of corporate galas, weddings and conferences. Every one of them has a duty of care to attendees — and that is where certified event security enters.",
      "Our event teams specialize in crowd flow modelling, choke-point prevention, wristband and bag-check protocols, credentialed access zones, medical liaison, and calm de-escalation of intoxicated or hostile guests. Every event supervisor holds AED / First-Aid certification and coordinates with local police, Ambulance NB and venue management before doors open.",
      "From 200-person private weddings to 30,000-attendee outdoor festivals, New Brunswick Security Services scales seamlessly. We also handle overnight equipment security so your stage, lights and vendor booths are guarded from load-in to load-out.",
    ],
    bullets: [
      "Crowd flow & capacity planning consultations",
      "Bag search, wristband, VIP & backstage credential control",
      "Medical & AED-equipped supervisors on every shift",
      "Overnight load-in / load-out equipment protection",
      "Experienced with Harvest Jazz, Area 506, Shediac Lobster Festival & more",
    ],
    keywords: [
      "event security New Brunswick",
      "concert security Fredericton",
      "festival security Moncton",
      "wedding security Saint John",
      "crowd control company NB",
    ],
  },
  {
    slug: "construction-site-security",
    name: "Construction Site Security",
    tagline: "Copper theft, tool loss, trespass — solved.",
    summary:
      "Overnight guard posts, mobile sweeps and mobile CCTV towers for New Brunswick construction sites. Insurance-approved deterrence.",
    long: [
      "Construction sites in Fredericton, Saint John, Moncton and Dieppe report six-figure annual losses to copper theft, tool disappearance and material damage. Our construction security division combines uniformed overnight gate posts, marked mobile patrol sweeps and portable solar CCTV surveillance towers so your project stays on budget and on schedule.",
      "We work directly with general contractors and project managers on every major NB infrastructure project — hospitals, schools, wind farms, LNG facilities, highway expansions and multi-family housing. Every site receives a written Security Management Plan compliant with WorkSafeNB standards and most builder's-risk insurance requirements.",
      "Our officers understand construction sites: they know why a scissor lift moved, why an idling truck at 03:00 is a red flag, and why an unlocked conex box at shift change is not acceptable. Their presence typically pays for itself in the first prevented theft.",
    ],
    bullets: [
      "Overnight fixed guard posts + mobile sweep combo",
      "Portable solar CCTV towers with live monitoring",
      "Builder's-risk & WorkSafeNB compliant documentation",
      "Copper, tool & fuel theft specialists",
      "Site Security Plans provided in writing",
    ],
    keywords: [
      "construction site security New Brunswick",
      "construction security guards Fredericton",
      "site security Moncton",
      "job site security Saint John",
      "copper theft prevention NB",
    ],
  },
  {
    slug: "residential-security",
    name: "Residential Security",
    tagline: "Estates, condos, gated communities, private homes.",
    summary:
      "Residential concierge, roving patrol and estate protection for high-net-worth families and condo boards across New Brunswick.",
    long: [
      "High-net-worth families, executives and expatriates relocating to New Brunswick trust us with the protection of their homes, staff and lifestyles. We provide discreet residential security ranging from single-family estate protection in Rothesay and Quispamsis to full concierge desks in downtown Fredericton and uptown Saint John condominium towers.",
      "Our residential officers are trained in situational awareness, family privacy protocols, visitor vetting, package management, mail-fraud prevention and emergency medical response. They are not the loud, uniformed presence of a construction site — they are the quiet, credentialed, ex-military or ex-policing professionals you barely notice until the moment you need them.",
      "For condo boards and property management companies we handle full concierge desks, tenant complaints, tow enforcement, incident reporting and elevator lockdown response — a full-service solution that improves property values and tenant satisfaction.",
    ],
    bullets: [
      "Estate protection & family privacy specialists",
      "Concierge desks for luxury condos & apartments",
      "Ex-military & ex-policing officers available",
      "Visitor vetting, package handling & tow enforcement",
      "Fully bonded, insured & confidentiality-agreement backed",
    ],
    keywords: [
      "residential security New Brunswick",
      "home security guards NB",
      "condo concierge Fredericton",
      "estate security Saint John",
      "gated community security Moncton",
    ],
  },
  {
    slug: "commercial-retail-security",
    name: "Commercial & Retail Security",
    tagline: "Loss prevention, opening/closing, plainclothes officers.",
    summary:
      "Retail store security, plainclothes loss-prevention officers and after-hours commercial guarding for New Brunswick businesses.",
    long: [
      "Retail shrink now costs Canadian retailers more than $10 billion annually. Our commercial and retail security division deploys both uniformed deterrent officers and plainclothes loss-prevention specialists into supermarkets, big-box stores, cannabis retailers, jewellery boutiques and shopping centres across New Brunswick.",
      "Plainclothes officers work the floor observing behaviours, following NB Provincial Court-approved apprehension protocols and only detaining after all six elements of theft are witnessed. Every apprehension is documented, and evidence packages are prepared for police handover, dramatically increasing conviction rates.",
      "For commercial property owners we deliver opening / closing officers, bank-run escorts, alarm response, key holding and after-hours patrol services from Woodstock to Shediac.",
    ],
    bullets: [
      "Uniformed & plainclothes loss-prevention officers",
      "Opening / closing & bank-run escort services",
      "Court-ready evidence packages for apprehensions",
      "Cannabis, jewellery & big-box retail experience",
      "Reduce shrink by an average of 42% in the first 6 months",
    ],
    keywords: [
      "retail security New Brunswick",
      "loss prevention Fredericton",
      "store security Moncton",
      "shopping mall security Saint John",
      "commercial security services NB",
    ],
  },
  {
    slug: "concierge-security",
    name: "Concierge Security",
    tagline: "First impression. Last defence.",
    summary:
      "Front-desk concierge security for hotels, corporate lobbies and luxury residential — the seamless blend of hospitality and protection.",
    long: [
      "Concierge security is the sophisticated fusion of hospitality and protection. Our concierge officers are groomed, uniformed in business attire, trained in etiquette and armed only with radios, incident logs and total situational awareness. They are the first face your visitors see and the last line of defence against unauthorized entry.",
      "Duties include visitor sign-in, deliveries, mail sorting, key management, taxi call, elevator control, incident documentation and calm intervention with disruptive individuals. All concierge staff are provincially licensed and undergo additional hospitality training — because a security guard who cannot smile is a liability in a lobby.",
      "Deployed across boutique hotels in Fredericton, corporate headquarters in Saint John and luxury residential towers in Moncton, our concierge program blends invisibility with impact.",
    ],
    bullets: [
      "Business attire, hospitality-trained officers",
      "Visitor & vendor management systems",
      "Mail, package & key handling protocols",
      "Discreet incident logging & escalation trees",
      "Perfect for hotels, HQs & luxury condos",
    ],
    keywords: [
      "concierge security New Brunswick",
      "hotel security Fredericton",
      "lobby security guards Saint John",
      "corporate concierge Moncton",
      "building concierge NB",
    ],
  },
  {
    slug: "corporate-security",
    name: "Corporate Security",
    tagline: "Executive floors. Reception. Boardroom protection.",
    summary:
      "Corporate security officers, executive protection and workplace-violence prevention for New Brunswick head-offices and campuses.",
    long: [
      "Corporate security is more than a receptionist with a radio. It is threat modelling, workplace-violence prevention, insider-threat mitigation, executive escort, boardroom sweeps and rapid response to bomb threats, active-threat calls and hostile terminations.",
      "New Brunswick Security Services partners with HR and legal teams at NB Power, Cooke Aquaculture, JD Irving and mid-market employers to design threat-response protocols that keep executives, staff and visitors safe. Our officers are Workplace Violence Prevention (Bill 168 equivalent) certified and understand the delicate choreography of a wrongful-dismissal walkout.",
      "Whether you need a fixed corporate reception, roving campus patrol or a two-officer team on a sensitive termination day, our corporate division scales to your requirement.",
    ],
    bullets: [
      "Workplace violence prevention & termination escorts",
      "Reception, mailroom & boardroom protection",
      "Threat assessments & security master planning",
      "Bomb-threat & active-assailant response training",
      "Aligned to Government of Canada TRA methodology",
    ],
    keywords: [
      "corporate security New Brunswick",
      "office security guards NB",
      "workplace violence prevention Fredericton",
      "termination security Saint John",
      "executive security Moncton",
    ],
  },
  {
    slug: "loss-prevention",
    name: "Loss Prevention",
    tagline: "Shrink reduction. Court-ready apprehensions.",
    summary:
      "Plainclothes loss-prevention officers, internal-theft investigations and inventory-shrink audits for NB retailers.",
    long: [
      "Loss prevention is a science, not a hunch. Our LP officers use CCTV coordination, floor surveillance, exception-based reporting review and legally-defensible apprehension protocol to reduce your shrink and prosecute offenders.",
      "Every LP officer is trained on the six elements of theft observation, Canadian Criminal Code section 494 citizen's arrest limits, and NB Provincial Court evidence standards. We work with your point-of-sale system exports to identify sweethearting, refund fraud and cash-register manipulation.",
      "Our LP team has successfully closed internal-theft investigations at grocery, cannabis, jewellery and hardware retailers across New Brunswick with a 94% prosecution success rate.",
    ],
    bullets: [
      "Plainclothes floor surveillance & apprehension",
      "Internal-theft & POS-fraud investigations",
      "Exception-based reporting review",
      "Section 494 compliant apprehension procedures",
      "94% prosecution success rate on closed cases",
    ],
    keywords: [
      "loss prevention New Brunswick",
      "shoplifting prevention Fredericton",
      "internal theft investigation Saint John",
      "retail LP Moncton",
      "plainclothes security NB",
    ],
  },
  {
    slug: "alarm-response",
    name: "Alarm Response",
    tagline: "First on scene. Every time. Any hour.",
    summary:
      "24/7 licensed alarm response and key-holder service for New Brunswick businesses and residential properties.",
    long: [
      "When your alarm activates at 03:12 on a Tuesday, you do not want to drive across town in pajamas. Our alarm response service dispatches a marked patrol vehicle to your site, verifies the cause of activation, confirms with your monitoring station and — if the site is secure — resets and re-locks the premises.",
      "If forced entry, damage or intruders are found, our officer holds the scene, coordinates with RCMP / municipal police, documents everything photographically and stays on-site until the site is either physically secured or a replacement key-holder arrives.",
      "Response times average 12 minutes in urban New Brunswick and 25 minutes in rural regions. Every response is logged, GPS-verified and delivered as a report the following morning.",
    ],
    bullets: [
      "24 / 7 / 365 monitoring station integration",
      "12-minute average urban response",
      "Police coordination & scene preservation",
      "Key-holder service — you sleep, we drive",
      "Full photo & narrative report every response",
    ],
    keywords: [
      "alarm response New Brunswick",
      "keyholder service NB",
      "24 hour alarm response Fredericton",
      "alarm response Moncton",
      "alarm dispatch Saint John",
    ],
  },
  {
    slug: "vip-executive-protection",
    name: "VIP & Executive Protection",
    tagline: "Discreet close protection. Advance planning.",
    summary:
      "Executive protection agents, secure ground transport and advance travel security for VIPs visiting or residing in New Brunswick.",
    long: [
      "VIP and executive protection is the pinnacle of our craft. Our close-protection agents are ex-CAF, ex-RCMP or ex-municipal SWAT with additional certification from ISPRA, ESI or Gavin de Becker & Associates programs. They are trained in defensive driving, medical trauma response, threat assessment and social-media OSINT to keep principals safe from the boardroom to the ballroom.",
      "We plan visits by film crews, corporate executives, dignitaries and public figures to New Brunswick — advance venue sweeps, route mapping, hospital pre-alerts, hotel floor-clearance and secure ground transport with armoured or non-armoured vehicles as required.",
      "Every EP engagement is preceded by a written Threat & Vulnerability Assessment and covered by a Non-Disclosure Agreement. Confidentiality is absolute.",
    ],
    bullets: [
      "Ex-CAF, ex-RCMP, ISPRA / ESI certified agents",
      "Defensive driving & armoured vehicle options",
      "Advance work, route planning & venue sweeps",
      "Medical trauma & tactical first-aid trained",
      "NDA-bound, confidential engagement",
    ],
    keywords: [
      "executive protection New Brunswick",
      "close protection NB",
      "VIP security Fredericton",
      "bodyguard services Moncton",
      "close protection agent Saint John",
    ],
  },
  {
    slug: "fire-watch",
    name: "Fire Watch",
    tagline: "Compliance-driven. Insurance-approved.",
    summary:
      "Fire watch officers dispatched when your sprinkler or fire-alarm system is impaired — NFPA & NB Fire Marshal compliant.",
    long: [
      "When your fire suppression or detection system is offline for maintenance, testing or repair, provincial regulation and NFPA 101 require a trained fire watch officer on continuous patrol. Our fire watch division deploys within 4 hours anywhere in New Brunswick with full documentation ready for the Office of the Fire Marshal.",
      "Officers are equipped with radios, torches, thermometers where required, and detailed patrol logs. We know the difference between a 30-minute sprinkler impairment and a 24-hour hot-work shutdown — and we bill accordingly.",
      "Fire watch is not optional. A missed patrol log can void your building insurance overnight. Trust the NB team that has completed over 6,000 documented fire watch shifts.",
    ],
    bullets: [
      "NFPA 101 & NB Fire Marshal compliant",
      "4-hour deployment province-wide",
      "Detailed 15-minute interval patrol logs",
      "Hot-work, sprinkler & alarm impairment coverage",
      "6,000+ documented shifts completed",
    ],
    keywords: [
      "fire watch New Brunswick",
      "fire watch guards NB",
      "sprinkler impairment guards Fredericton",
      "fire watch services Moncton",
      "hot work fire watch Saint John",
    ],
  },
  {
    slug: "parking-enforcement",
    name: "Parking Enforcement",
    tagline: "By-law-compliant tickets. Tow authorization. Fair.",
    summary:
      "Private-property parking enforcement, ticketing and tow-authorization for NB landlords and property managers.",
    long: [
      "Unauthorized parking costs commercial and residential landlords tenant satisfaction, emergency-access compromise and re-paving budget. Our parking enforcement officers patrol contracted lots, issue provincially-recognized private-property tickets and authorize tows in accordance with your Site Traffic Plan and the NB Motor Vehicle Act.",
      "Every ticket is photo-documented (plate, VIN when accessible, position, time) and appealable through our online portal to remain compliant with Small Claims Court expectations. Our officers de-escalate confrontations calmly — because a parking ticket is not worth an assault charge.",
      "Deployed at hospitals, condo boards, retail plazas and industrial sites across New Brunswick.",
    ],
    bullets: [
      "Photo-documented, appealable private tickets",
      "Tow authorization under signed towing contracts",
      "De-escalation trained officers",
      "Custom Site Traffic Plans included",
      "Web portal for tenant / owner appeals",
    ],
    keywords: [
      "parking enforcement New Brunswick",
      "private parking tickets NB",
      "parking security Fredericton",
      "tow enforcement Saint John",
      "parking lot security Moncton",
    ],
  },
  {
    slug: "cctv-monitoring",
    name: "CCTV Monitoring",
    tagline: "Live eyes. AI-triggered alerts. Human verification.",
    summary:
      "Live remote video monitoring, AI-triggered alerts, virtual guard tours and post-incident forensic review for NB properties.",
    long: [
      "CCTV that no one watches is a very expensive doorbell. Our live remote monitoring centre pairs artificial-intelligence triggered analytics with human verification operators to reduce false alarms by 96% and dispatch mobile patrol or police only when a real threat is confirmed on video.",
      "Services include virtual guard tours (scheduled camera-based patrol sweeps replacing part of your on-site guard cost), talk-down speakers that let operators verbally challenge intruders in real time, and forensic post-incident video pulls for insurance and prosecution.",
      "Compatible with Hikvision, Axis, Milestone, Genetec, Avigilon and virtually every enterprise VMS deployed in New Brunswick.",
    ],
    bullets: [
      "AI analytics + human verification (96% fewer false alarms)",
      "Live talk-down speaker intervention",
      "Virtual guard tours reduce on-site guard cost by up to 60%",
      "Forensic pulls for insurance & police",
      "Works with Hikvision, Axis, Milestone, Genetec & Avigilon",
    ],
    keywords: [
      "CCTV monitoring New Brunswick",
      "remote video monitoring NB",
      "virtual guard tours Fredericton",
      "live CCTV monitoring Moncton",
      "video surveillance Saint John",
    ],
  },
  {
    slug: "mining-security",
    name: "Mining Security Services",
    tagline: "Pit protection. Camp control. High-value ore custody.",
    summary:
      "Specialized security for New Brunswick mine sites, ore-processing facilities, exploration camps and haul-road corridors — from potash operations in Sussex to base-metal deposits in the Bathurst Mining Camp.",
    long: [
      "New Brunswick has one of Canada's richest mining legacies — from the Bathurst Mining Camp (once the world's largest zinc-lead-silver producer), to the potash formations under Sussex, Penobsquis and Cassidy Lake, to graphite, antimony, tungsten and rare-earth projects across the province. Every one of these operations presents a security challenge unlike any other industry: valuable ore concentrate, remote pit locations, high-voltage electrical infrastructure, cyanide and reagent inventories, camp populations, and 24/7 haul-truck movements that never stop.",
      "Our mining security division deploys purpose-trained officers who understand MSHA-equivalent WorkSafeNB Underground Mine Regulation 96-105, magazine (explosives storage) regulations, cyanide code protocols, and ore-thin (high-grade concentrate) diversion patterns. Every officer assigned to a mining contract completes site-specific hazard orientation, receives underground refuge-station training where applicable, and works integrated shift patterns matching the mine's four-crew rotation.",
      "Services include gatehouse access control with weigh-scale integration, employee bus escorts to remote camps, magazine and cyanide-shed patrol, contractor and vendor vetting, ore-truck seal verification, exploration-camp overnight guarding, tailings-pond perimeter checks, and rapid response to trespass, poaching and eco-protest incidents on active claims. We also provide loss-prevention investigators specialized in high-grade ore theft, employee-diversion schemes, and contractor time-fraud on remote fly-in-fly-out projects.",
      "Whether you operate an established underground mine in the Chaleur region, a fresh open-pit project in Restigouche, an advanced-exploration camp in the highlands, or a critical-minerals ore-processing plant near the Port of Belledune, New Brunswick Security Services is the mining partner that speaks fluent operations — 24 hours a day, 365 days a year, blackflies, whiteouts, and hostile pickets included.",
    ],
    bullets: [
      "MSHA-equivalent & WorkSafeNB Underground Mine Regulation trained officers",
      "Magazine (explosives), cyanide-shed & reagent inventory patrol",
      "Ore-concentrate & haul-truck seal verification",
      "Exploration camp overnight guarding (fly-in / fly-out ready)",
      "Anti-picket, eco-protest & anti-poaching perimeter response",
      "Bilingual (English / French) roster for northern & Acadian sites",
    ],
    keywords: [
      "mining security services New Brunswick",
      "mine site security NB",
      "potash mine security Sussex",
      "Bathurst Mining Camp security",
      "exploration camp security New Brunswick",
      "mine security guards Miramichi",
      "ore theft prevention NB",
      "tailings pond security Bathurst",
    ],
  },
];

export const CITIES = [
  {
    slug: "fredericton",
    name: "Fredericton",
    region: "Capital Region",
    population: "63,116",
    coords: "45.9636° N, 66.6431° W",
    landmarks: ["Beaverbrook Art Gallery", "The Legislative Assembly", "University of New Brunswick", "Regent Mall", "Historic Garrison District"],
    intro:
      "As the capital of New Brunswick, Fredericton demands the highest standard of security. From the Legislative Assembly to the historic Garrison District, from Regent Mall to the UNB campus, our Fredericton team protects government facilities, commercial retail, residential estates and public events across the greater capital region.",
    sectors: ["Government & Public Sector", "Higher Education", "Retail & Hospitality", "Residential Estates", "Cultural Events"],
    hero: "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "saint-john",
    name: "Saint John",
    region: "Bay of Fundy",
    population: "69,895",
    coords: "45.2733° N, 66.0633° W",
    landmarks: ["Uptown Saint John", "Reversing Falls", "Irving Oil Refinery", "Saint John Sea Dogs Arena", "Port Saint John"],
    intro:
      "Canada's oldest incorporated city and New Brunswick's largest industrial hub. Saint John's port operations, the Irving refinery, uptown corporate towers and cruise-ship terminal all demand elite security. Our Saint John division specializes in port, industrial and corporate protection along the Bay of Fundy.",
    sectors: ["Port & Marine", "Petro-chemical & Industrial", "Corporate & Financial", "Cruise & Tourism", "Uptown Retail"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "moncton",
    name: "Moncton",
    region: "Greater Moncton",
    population: "79,470",
    coords: "46.0878° N, 64.7782° W",
    landmarks: ["Magnetic Hill", "Avenir Centre", "Champlain Place", "Casino New Brunswick", "Moncton Coliseum"],
    intro:
      "The commercial and transportation hub of the Maritimes. Home to Champlain Place — Atlantic Canada's largest shopping centre — Avenir Centre concerts, Casino New Brunswick and a dense corporate downtown. Our Moncton team delivers retail LP, event security, casino support and corporate protection across the Greater Moncton region.",
    sectors: ["Retail & Shopping Centres", "Casino & Entertainment", "Concerts & Sports", "Corporate Downtown", "Logistics & Transport"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "dieppe",
    name: "Dieppe",
    region: "Greater Moncton",
    population: "28,114",
    coords: "46.0989° N, 64.7242° W",
    landmarks: ["Champlain Place border", "Dieppe Aquatic Centre", "Dieppe Market", "Ecole Sainte-Thérèse"],
    intro:
      "Officially bilingual and rapidly growing, Dieppe is home to Atlantic Canada's largest shopping district border and a booming residential development sector. We provide French-language uniformed guards, retail LP and construction site security across Dieppe's expanding commercial corridors.",
    sectors: ["Retail & Shopping", "Bilingual Concierge", "Residential Growth", "Construction Sites"],
    hero: "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "riverview",
    name: "Riverview",
    region: "Greater Moncton",
    population: "20,584",
    coords: "46.0611° N, 64.8047° W",
    landmarks: ["Coverdale Road", "Riverview Arts Centre", "Petitcodiac River waterfront"],
    intro:
      "The peaceful residential complement to Moncton and Dieppe. Riverview families and property owners trust us with home patrol, estate protection and event security along the Petitcodiac.",
    sectors: ["Residential Patrol", "Estate Protection", "Community Events", "Retail"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "miramichi",
    name: "Miramichi",
    region: "Northumberland",
    population: "17,537",
    coords: "47.0281° N, 65.5006° W",
    landmarks: ["Ritchie Wharf", "Middle Island Historic Site", "Miramichi Regional Hospital", "Irving pulp mill"],
    intro:
      "The salmon-fishing capital of the world and home to major forestry, healthcare and marine operations. Our Miramichi division specializes in industrial site protection, hospital security and rural mobile patrol across Northumberland County.",
    sectors: ["Forestry & Pulp Mills", "Healthcare", "Rural Patrol", "Marine Wharf"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "edmundston",
    name: "Edmundston",
    region: "Madawaska",
    population: "16,437",
    coords: "47.3737° N, 68.3251° W",
    landmarks: ["Foire Brayonne Festival grounds", "Twin Rivers Paper Mill", "Université de Moncton campus"],
    intro:
      "The heart of the Republic of Madawaska. Predominantly Francophone, Edmundston hosts one of Atlantic Canada's largest paper mills and the Foire Brayonne festival. We provide French-speaking guards, industrial security and event protection.",
    sectors: ["Pulp & Paper", "Bilingual Events", "Border & Trucking", "Retail"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "bathurst",
    name: "Bathurst",
    region: "Chaleur",
    population: "12,157",
    coords: "47.6187° N, 65.6510° W",
    landmarks: ["Chaleur Regional Hospital", "K.C. Irving Regional Centre", "Bathurst Marina", "Downing Street shopping"],
    intro:
      "The service centre of northern New Brunswick along the Bay of Chaleur. Home to hospitals, mining logistics and the Titan hockey arena. Our Bathurst team handles industrial, healthcare and event security.",
    sectors: ["Mining & Industrial", "Hospital", "Sports Arena", "Bilingual Retail"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "campbellton",
    name: "Campbellton",
    region: "Restigouche",
    population: "6,883",
    coords: "48.0056° N, 66.6733° W",
    landmarks: ["Sugarloaf Provincial Park", "Salmon Plaza", "Campbellton Regional Hospital", "J.C. Van Horne Bridge to Quebec"],
    intro:
      "The gateway between New Brunswick and Quebec's Gaspé peninsula. Cross-border logistics, healthcare and outdoor tourism drive our Campbellton operations.",
    sectors: ["Cross-border Logistics", "Healthcare", "Tourism & Recreation", "Bilingual Retail"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "oromocto",
    name: "Oromocto",
    region: "Capital Region",
    population: "9,223",
    coords: "45.8422° N, 66.4794° W",
    landmarks: ["CFB Gagetown", "Base Gagetown Golf Club", "Oromocto Public Hospital"],
    intro:
      "Home to CFB Gagetown — Canada's second-largest military base. Our Oromocto team includes ex-CAF officers and specializes in perimeter, contractor and construction security around the base.",
    sectors: ["Military-adjacent Contractors", "Construction", "Residential", "Retail"],
    hero: "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "sackville",
    name: "Sackville",
    region: "Tantramar",
    population: "5,331",
    coords: "45.8973° N, 64.3688° W",
    landmarks: ["Mount Allison University", "Sackville Waterfowl Park", "Tantramar Regional High School"],
    intro:
      "The picturesque university town at the border with Nova Scotia. Home to Mount Allison University, Sackville needs campus security, student event coverage and residential estate protection.",
    sectors: ["Higher Education", "Student Housing", "Campus Events", "Border Retail"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "sussex",
    name: "Sussex",
    region: "Kings",
    population: "4,282",
    coords: "45.7238° N, 65.5069° W",
    landmarks: ["Sussex Balloon Fiesta", "PotashCorp Penobsquis", "Kings County Museum"],
    intro:
      "The dairy capital of New Brunswick and home to the world-famous Balloon Fiesta. Our Sussex team handles balloon-festival event security, agricultural site protection and mining operations security.",
    sectors: ["Festivals & Balloon Fiesta", "Agriculture & Dairy", "Mining", "Retail"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "woodstock",
    name: "Woodstock",
    region: "Carleton",
    population: "5,228",
    coords: "46.1523° N, 67.6014° W",
    landmarks: ["Old Carleton County Court House", "AYR Motor Centre", "Woodstock First Nation"],
    intro:
      "The service centre of Carleton County and gateway to Maine. Cross-border trucking, agriculture and rural retail define our Woodstock security operations.",
    sectors: ["Cross-border Logistics", "Agriculture", "First-Nation Liaison", "Retail"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "grand-bay-westfield",
    name: "Grand Bay-Westfield",
    region: "Greater Saint John",
    population: "5,117",
    coords: "45.3653° N, 66.2378° W",
    landmarks: ["Nerepis Marsh", "Westfield Ferry", "Kennebecasis River waterfront"],
    intro:
      "The quiet commuter town northwest of Saint John along the Kennebecasis River. Residential patrol, marina protection and estate security define our Grand Bay-Westfield mandate.",
    sectors: ["Residential Estates", "Marina & Waterfront", "Commuter Community", "Small Business"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "quispamsis",
    name: "Quispamsis",
    region: "Kennebecasis Valley",
    population: "18,768",
    coords: "45.4321° N, 65.9490° W",
    landmarks: ["Qplex Recreational Complex", "Meenan's Cove Beach Park"],
    intro:
      "One of the fastest-growing communities in New Brunswick, and one of the wealthiest per-capita. Quispamsis families rely on us for estate protection, gated-community concierge and event security at the Qplex.",
    sectors: ["High-Net-Worth Residential", "Estate Protection", "Family Events", "Retail"],
    hero: "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "rothesay",
    name: "Rothesay",
    region: "Kennebecasis Valley",
    population: "11,659",
    coords: "45.3833° N, 66.0000° W",
    landmarks: ["Rothesay Netherwood School", "Rothesay Common", "Renforth waterfront"],
    intro:
      "Historically the wealthiest suburb of Saint John. Home to Rothesay Netherwood School and generations of Maritime business families. We provide discreet estate protection, private-school liaison and family close-protection services.",
    sectors: ["High-Net-Worth Residential", "Private Schools", "Family Protection", "Waterfront Estates"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "shediac",
    name: "Shediac",
    region: "Southeastern NB",
    population: "6,943",
    coords: "46.2189° N, 64.5397° W",
    landmarks: ["World's Largest Lobster", "Parlee Beach Provincial Park", "Shediac Lobster Festival grounds"],
    intro:
      "The lobster capital of the world and a summer tourism magnet. Our Shediac division handles Lobster Festival crowd control, beach patrol assistance and cottage / marina security.",
    sectors: ["Tourism & Festivals", "Beaches & Parks", "Cottages & Marinas", "Bilingual Retail"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "caraquet",
    name: "Caraquet",
    region: "Acadian Peninsula",
    population: "4,248",
    coords: "47.7942° N, 64.9375° W",
    landmarks: ["Village Historique Acadien", "Festival Acadien de Caraquet", "Caraquet Harbour"],
    intro:
      "The cultural capital of Acadie. Home to the Festival Acadien and a proud Francophone community. Our fully bilingual Caraquet team specializes in festival security, harbour protection and Village Historique event coverage.",
    sectors: ["Acadian Cultural Events", "Harbour & Fisheries", "Tourism", "Bilingual Retail"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "beresford",
    name: "Beresford",
    region: "Chaleur",
    population: "4,351",
    coords: "47.6987° N, 65.7167° W",
    landmarks: ["Beresford Beach", "Bay of Chaleur waterfront", "Danny's Inn"],
    intro:
      "A bilingual coastal community on the Bay of Chaleur just north of Bathurst. Our Beresford team handles beachfront event patrol, marina protection and residential coverage.",
    sectors: ["Beach & Marina", "Bilingual Retail", "Residential", "Tourism"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "belledune",
    name: "Belledune",
    region: "Chaleur",
    population: "1,388",
    coords: "47.9057° N, 65.8500° W",
    landmarks: ["Port of Belledune", "Belledune Generating Station", "Jacquet River"],
    intro:
      "Deep-water port and industrial powerhouse of northern NB. Belledune's port terminal, generating station and heavy-industry logistics demand specialized industrial security which our team delivers day and night.",
    sectors: ["Port & Marine", "Power Generation", "Heavy Industry", "Logistics"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "dalhousie",
    name: "Dalhousie",
    region: "Restigouche",
    population: "3,126",
    coords: "48.0654° N, 66.3737° W",
    landmarks: ["Restigouche River waterfront", "Inch Arran Point Lighthouse", "Bon Ami Rocks"],
    intro:
      "A historic bilingual town on the Restigouche River across from Quebec. We provide cross-border logistics security, waterfront and event coverage.",
    sectors: ["Cross-border Logistics", "Waterfront", "Tourism", "Bilingual Retail"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "atholville",
    name: "Atholville",
    region: "Restigouche",
    population: "1,383",
    coords: "47.9948° N, 66.7202° W",
    landmarks: ["AV Cell Mill", "Restigouche River trail"],
    intro:
      "A small industrial community adjoining Campbellton. Our team secures the AV Cell mill site, cross-border corridors and rural properties across Atholville.",
    sectors: ["Pulp & Paper", "Rural Patrol", "Bilingual Services"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "tide-head",
    name: "Tide Head",
    region: "Restigouche",
    population: "1,022",
    coords: "48.0206° N, 66.7834° W",
    landmarks: ["Tide Head trail system", "Restigouche estuary"],
    intro:
      "A quiet Restigouche village where the tide finally meets the river. Residential patrol and outdoor recreation event security are our specialities here.",
    sectors: ["Residential Patrol", "Rural Estates", "Recreation Events"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "kedgwick",
    name: "Kedgwick",
    region: "Restigouche",
    population: "1,864",
    coords: "47.6423° N, 67.3468° W",
    landmarks: ["Kedgwick River", "Forestry operations"],
    intro:
      "A francophone forestry community deep in the Restigouche uplands. We deliver bilingual mill-site security, mobile patrol and rural alarm response.",
    sectors: ["Forestry", "Bilingual Services", "Mill Sites", "Rural Patrol"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "saint-quentin",
    name: "Saint-Quentin",
    region: "Restigouche West",
    population: "2,101",
    coords: "47.5115° N, 67.3919° W",
    landmarks: ["Foire Brayonne satellite", "Sugar bush forest"],
    intro:
      "The maple-syrup capital of Atlantic Canada and a proudly francophone community. Bilingual event security, forestry protection and rural patrol define our Saint-Quentin operations.",
    sectors: ["Maple & Agriculture", "Bilingual Events", "Forestry", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "saint-leonard",
    name: "Saint-Léonard",
    region: "Madawaska",
    population: "1,251",
    coords: "47.1633° N, 67.9236° W",
    landmarks: ["Saint John River waterfront", "Cross-border bridge to Maine"],
    intro:
      "A francophone border town across from Van Buren, Maine. Cross-border logistics, agricultural sites and bilingual retail define our Saint-Léonard mandate.",
    sectors: ["Cross-border Logistics", "Agriculture", "Bilingual Retail"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "grand-falls",
    name: "Grand Falls / Grand-Sault",
    region: "Madawaska",
    population: "5,326",
    coords: "47.0421° N, 67.7407° W",
    landmarks: ["Grand Falls Gorge", "Ron Turcotte Bridge", "Malabeam Reception Centre"],
    intro:
      "A bilingual town wrapped around one of Eastern Canada's most spectacular waterfalls. Tourism, potato-industry logistics and hospital security are our Grand Falls specialities.",
    sectors: ["Tourism", "Agriculture & Potato Industry", "Healthcare", "Bilingual Retail"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "haut-madawaska",
    name: "Haut-Madawaska",
    region: "Madawaska",
    population: "3,952",
    coords: "47.4022° N, 68.6003° W",
    landmarks: ["Clair border crossing", "Fort Ingall replica"],
    intro:
      "A newly amalgamated francophone region on the Quebec border. We provide bilingual border-adjacent security, forestry site protection and rural patrol.",
    sectors: ["Border Logistics", "Forestry", "Bilingual Services", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "perth-andover",
    name: "Perth-Andover",
    region: "Victoria",
    population: "1,590",
    coords: "46.7473° N, 67.7062° W",
    landmarks: ["Saint John River", "Tobique Narrows Dam"],
    intro:
      "A twin village straddling the Saint John River in Victoria County. Our team handles agricultural site security, hydro-facility perimeter checks and community events.",
    sectors: ["Agriculture", "Hydro Facilities", "Rural Patrol", "Community Events"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "plaster-rock",
    name: "Plaster Rock",
    region: "Victoria",
    population: "1,033",
    coords: "46.9016° N, 67.4030° W",
    landmarks: ["World Pond Hockey Championship rink", "Tobique River"],
    intro:
      "The Tobique-Nictau home of the World Pond Hockey Championship. Winter event security and rural mobile patrol are core to our Plaster Rock service.",
    sectors: ["Winter Events", "Forestry", "Rural Patrol", "Tourism"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "florenceville-bristol",
    name: "Florenceville-Bristol",
    region: "Carleton",
    population: "1,637",
    coords: "46.4433° N, 67.6116° W",
    landmarks: ["McCain Foods HQ", "Potato World Museum", "Saint John River"],
    intro:
      "The global headquarters of McCain Foods and the self-declared 'French Fry Capital of the World.' We provide corporate HQ security, food-processing site protection and executive escort.",
    sectors: ["Corporate HQ", "Food Processing", "Executive Escort", "Agriculture"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "hartland",
    name: "Hartland",
    region: "Carleton",
    population: "969",
    coords: "46.2986° N, 67.5237° W",
    landmarks: ["Longest Covered Bridge in the World", "Saint John River"],
    intro:
      "Home to the world's longest covered bridge and a rich agricultural community. Our Hartland team handles heritage site protection, agricultural security and tourist event coverage.",
    sectors: ["Heritage Sites", "Agriculture", "Tourism", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "nackawic",
    name: "Nackawic-Millville",
    region: "York",
    population: "1,043",
    coords: "45.9959° N, 67.2409° W",
    landmarks: ["World's Largest Axe", "AV Nackawic pulp mill"],
    intro:
      "Home to the World's Largest Axe and a major pulp mill on the Saint John River. Mill site security and forestry logistics are our specialities.",
    sectors: ["Pulp Mill", "Forestry", "Rural Patrol", "Heritage Sites"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "mcadam",
    name: "McAdam",
    region: "York",
    population: "1,151",
    coords: "45.5895° N, 67.3271° W",
    landmarks: ["McAdam Railway Station (National Historic Site)", "Canadian Pacific rail yard"],
    intro:
      "A historic rail town on the Maine border. Cross-border logistics, rail yard security and heritage protection define our McAdam service.",
    sectors: ["Rail & Logistics", "Cross-border", "Heritage Sites", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "harvey",
    name: "Harvey",
    region: "York",
    population: "348",
    coords: "45.7333° N, 67.0000° W",
    landmarks: ["Harvey Lake", "Yoho Lake"],
    intro:
      "A rural community south of Fredericton famous for its lakes and farming heritage. Cottage, marina and estate security are our focus in Harvey.",
    sectors: ["Cottages & Marinas", "Agriculture", "Estate Protection"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "stanley",
    name: "Stanley",
    region: "York",
    population: "412",
    coords: "46.2833° N, 66.7333° W",
    landmarks: ["Stanley Village", "Miramichi Road"],
    intro:
      "A rural village north of Fredericton in the Nashwaak Valley. Forestry logistics, agricultural sites and community events are our specialities.",
    sectors: ["Forestry", "Agriculture", "Community Events", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "doaktown",
    name: "Doaktown",
    region: "Miramichi Valley",
    population: "793",
    coords: "46.5561° N, 66.1183° W",
    landmarks: ["Atlantic Salmon Museum", "Doak Historic Site"],
    intro:
      "The Atlantic Salmon capital on the Miramichi River. Fishing-lodge protection, event security and rural mobile patrol are our Doaktown speciality.",
    sectors: ["Fishing Lodges", "Tourism", "Rural Patrol", "Heritage Sites"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "blackville",
    name: "Blackville",
    region: "Miramichi Valley",
    population: "889",
    coords: "46.7333° N, 65.8333° W",
    landmarks: ["Main Southwest Miramichi River", "W.S. Loggie Building"],
    intro:
      "A historic salmon-fishing village along the Miramichi. Lodge protection and rural patrol services in the heart of the Miramichi salmon country.",
    sectors: ["Fishing Lodges", "Rural Patrol", "Tourism"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "neguac",
    name: "Neguac",
    region: "Northumberland",
    population: "1,573",
    coords: "47.2333° N, 65.0500° W",
    landmarks: ["Neguac Harbour", "Miramichi Bay marshes"],
    intro:
      "A francophone fishing village on Miramichi Bay. Bilingual harbour security, marina protection and seasonal event coverage are our Neguac focus.",
    sectors: ["Harbour & Fisheries", "Bilingual Services", "Tourism", "Marina"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "rogersville",
    name: "Rogersville",
    region: "Kent",
    population: "1,169",
    coords: "46.7314° N, 65.4262° W",
    landmarks: ["Notre-Dame-de-l'Assomption Monastery", "Rogersville Festival grounds"],
    intro:
      "A francophone community celebrated for its Acadian heritage. We deliver bilingual event security, monastery-adjacent patrol and rural coverage.",
    sectors: ["Bilingual Events", "Heritage Sites", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "richibucto",
    name: "Richibucto",
    region: "Kent",
    population: "1,266",
    coords: "46.6800° N, 64.8700° W",
    landmarks: ["Richibucto River", "Kouchibouguac National Park border"],
    intro:
      "A bilingual fishing town gateway to Kouchibouguac National Park. Our team handles park-adjacent tourism security, marina protection and harbour patrol.",
    sectors: ["Fisheries", "Tourism", "Park Adjacent", "Bilingual Services"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "bouctouche",
    name: "Bouctouche",
    region: "Kent",
    population: "2,361",
    coords: "46.4667° N, 64.7333° W",
    landmarks: ["Le Pays de la Sagouine", "Bouctouche Dune", "Irving Eco-Centre"],
    intro:
      "The Acadian tourism jewel of Kent County and birthplace of KC Irving. Bilingual event security, tourism site protection and dune-park patrol are our Bouctouche services.",
    sectors: ["Acadian Tourism", "Bilingual Events", "Dune & Park Patrol", "Retail"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "cap-acadie",
    name: "Cap-Acadie",
    region: "Southeastern NB",
    population: "7,753",
    coords: "46.2167° N, 64.2667° W",
    landmarks: ["Cap-Pelé beaches", "Fisheries wharves"],
    intro:
      "The newly amalgamated Acadian coastal region including Cap-Pelé. Bilingual seasonal event security, fisheries protection and beach patrol form the core mandate.",
    sectors: ["Fisheries", "Beach Tourism", "Bilingual Events", "Retail"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "memramcook",
    name: "Memramcook",
    region: "Southeastern NB",
    population: "4,778",
    coords: "46.0000° N, 64.5500° W",
    landmarks: ["Monument-Lefebvre National Historic Site", "Université Saint-Joseph ruins"],
    intro:
      "The cradle of the Acadian Renaissance. Heritage-site protection, bilingual event security and rural patrol define our Memramcook operations.",
    sectors: ["Heritage Sites", "Acadian Events", "Rural Patrol", "Bilingual Services"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "salisbury",
    name: "Salisbury",
    region: "Greater Moncton",
    population: "2,483",
    coords: "46.0389° N, 65.0431° W",
    landmarks: ["Salisbury Big Stop", "Petitcodiac River"],
    intro:
      "A growing commuter village just west of Moncton along the Trans-Canada Highway. Retail, logistics and residential security define our Salisbury service.",
    sectors: ["Logistics & Trucking", "Retail", "Residential Patrol"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "petitcodiac",
    name: "Petitcodiac",
    region: "Westmorland",
    population: "1,376",
    coords: "45.9333° N, 65.1667° W",
    landmarks: ["Old Bank Museum", "Historic Grist Mill"],
    intro:
      "A historic Westmorland village on the Trans-Canada. Heritage protection, agricultural site security and rural patrol.",
    sectors: ["Heritage Sites", "Agriculture", "Rural Patrol"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "hillsborough",
    name: "Hillsborough",
    region: "Albert",
    population: "1,335",
    coords: "45.9167° N, 64.6333° W",
    landmarks: ["Salem & Hillsborough Railroad", "William Henry Steeves House"],
    intro:
      "A historic village in Albert County on the Petitcodiac. Heritage site security, tourist attraction protection and rural patrol.",
    sectors: ["Heritage Sites", "Tourism", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "alma",
    name: "Alma / Fundy Albert",
    region: "Albert",
    population: "232",
    coords: "45.6001° N, 64.9500° W",
    landmarks: ["Fundy National Park gateway", "Alma Beach", "World's highest tides"],
    intro:
      "The gateway to Fundy National Park and the world's highest tides. Park-adjacent tourism security, cottage protection and seasonal event coverage.",
    sectors: ["Park Adjacent", "Tourism", "Cottages", "Marina"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "riverside-albert",
    name: "Riverside-Albert",
    region: "Albert",
    population: "310",
    coords: "45.7333° N, 64.7500° W",
    landmarks: ["Cape Enrage", "Mary's Point migratory reserve"],
    intro:
      "A quiet Fundy village near Cape Enrage. Tourism site security, coastal patrol and estate protection define our Riverside-Albert service.",
    sectors: ["Tourism", "Coastal Patrol", "Rural Estates"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "hampton",
    name: "Hampton",
    region: "Kings",
    population: "4,289",
    coords: "45.5292° N, 65.8500° W",
    landmarks: ["Kennebecasis River", "Hampton Kings County Court House"],
    intro:
      "The shire-town of Kings County on the Kennebecasis River. Residential estates, courthouse-adjacent security and community event coverage define our Hampton service.",
    sectors: ["Residential Estates", "Government-Adjacent", "Community Events"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "norton",
    name: "Norton",
    region: "Kings",
    population: "1,197",
    coords: "45.6333° N, 65.7000° W",
    landmarks: ["Kingston Peninsula gateway", "Bell Meadows"],
    intro:
      "A rural Kings County village near the Kennebecasis. Estate protection, agricultural sites and rural mobile patrol.",
    sectors: ["Rural Estates", "Agriculture", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "chipman",
    name: "Chipman",
    region: "Queens",
    population: "1,104",
    coords: "46.1667° N, 65.9000° W",
    landmarks: ["Salmon River", "Chipman Forest Products"],
    intro:
      "A Queens County forestry village on the Salmon River. Mill site security, forestry logistics and rural patrol form the mandate.",
    sectors: ["Forestry", "Mill Sites", "Rural Patrol"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "minto",
    name: "Minto",
    region: "Queens",
    population: "2,406",
    coords: "46.0833° N, 66.0167° W",
    landmarks: ["Grand Lake", "Historic coal-mining heritage"],
    intro:
      "A Queens County community with deep coal-mining and industrial heritage on Grand Lake. Industrial site security and rural patrol are our specialities.",
    sectors: ["Industrial Heritage", "Grand Lake Marina", "Rural Patrol"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "cambridge-narrows",
    name: "Cambridge-Narrows",
    region: "Queens",
    population: "652",
    coords: "45.8000° N, 66.0000° W",
    landmarks: ["Washademoak Lake", "Cambridge Narrows Bridge"],
    intro:
      "A lakeside Queens County community. Cottage security, marina protection and estate patrol along Washademoak Lake.",
    sectors: ["Cottages & Marina", "Estate Protection", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "saint-andrews",
    name: "Saint Andrews",
    region: "Charlotte",
    population: "1,786",
    coords: "45.0731° N, 67.0530° W",
    landmarks: ["The Algonquin Resort", "Kingsbrae Garden", "Ministers Island"],
    intro:
      "The historic seaside resort town of Charlotte County — 'Saint Andrews by-the-Sea.' Luxury resort security, heritage site protection and yacht-club coverage define our mandate.",
    sectors: ["Luxury Tourism", "Heritage Sites", "Yacht & Marina", "Retail"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "saint-george",
    name: "Saint George",
    region: "Charlotte",
    population: "1,543",
    coords: "45.1300° N, 66.8267° W",
    landmarks: ["Magaguadavic River Falls", "Second oldest Protestant cemetery in Canada"],
    intro:
      "A historic Charlotte County town famous for its dramatic river gorge. Industrial site protection, marine logistics and heritage security.",
    sectors: ["Industrial", "Marine Logistics", "Heritage Sites"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "saint-stephen",
    name: "Saint Stephen",
    region: "Charlotte",
    population: "4,415",
    coords: "45.1958° N, 67.2778° W",
    landmarks: ["Chocolate Museum", "Ganong Bros. HQ", "International Bridge to Calais, Maine"],
    intro:
      "'Canada's Chocolate Town' and a busy US border crossing to Calais, Maine. Corporate HQ security, cross-border logistics and retail LP are our specialities.",
    sectors: ["Corporate HQ", "Cross-border Logistics", "Retail LP", "Heritage"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "campobello-island",
    name: "Campobello Island",
    region: "Charlotte",
    population: "874",
    coords: "44.8667° N, 66.9000° W",
    landmarks: ["Roosevelt Campobello International Park", "Head Harbour Lightstation"],
    intro:
      "The Fundy island where Franklin Roosevelt summered. Cross-border tourism security, heritage protection and island patrol define our Campobello mandate.",
    sectors: ["Heritage Tourism", "Cross-border", "Island Patrol", "Marina"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "shippagan",
    name: "Shippagan",
    region: "Acadian Peninsula",
    population: "2,580",
    coords: "47.7439° N, 64.7128° W",
    landmarks: ["Aquarium & Marine Centre", "Université de Moncton campus", "Fishing wharves"],
    intro:
      "The scientific and educational hub of the Acadian Peninsula. Bilingual campus security, aquarium event coverage and fishing-wharf protection.",
    sectors: ["Higher Education", "Aquarium & Tourism", "Fisheries", "Bilingual Events"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "lameque",
    name: "Lamèque-Miscou",
    region: "Acadian Peninsula",
    population: "6,241",
    coords: "47.7833° N, 64.6333° W",
    landmarks: ["Miscou Island Lighthouse", "Peat bogs", "Baroque Music Festival"],
    intro:
      "The island tip of the Acadian Peninsula and home to the International Baroque Music Festival. Bilingual event security, peat-bog industrial protection and marina patrol.",
    sectors: ["Bilingual Music Events", "Fisheries & Peat", "Marina & Lighthouse", "Tourism"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "tracadie",
    name: "Tracadie",
    region: "Acadian Peninsula",
    population: "16,144",
    coords: "47.5167° N, 64.9000° W",
    landmarks: ["Musée Historique de Tracadie", "Baie de Tracadie", "Regional Hospital"],
    intro:
      "The service centre of the Acadian Peninsula. Bilingual hospital security, retail LP, event coverage and industrial site protection.",
    sectors: ["Healthcare", "Bilingual Retail", "Community Events", "Industrial"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "bas-caraquet",
    name: "Bas-Caraquet",
    region: "Acadian Peninsula",
    population: "1,320",
    coords: "47.8085° N, 64.8404° W",
    landmarks: ["Bas-Caraquet Harbour", "Acadian fishing fleet"],
    intro:
      "A francophone fishing village adjacent to Caraquet. Bilingual harbour security, fleet protection and event coverage.",
    sectors: ["Fisheries", "Harbour", "Bilingual Events"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "petit-rocher",
    name: "Petit-Rocher",
    region: "Chaleur",
    population: "1,844",
    coords: "47.7833° N, 65.7167° W",
    landmarks: ["Musée des Mineurs de la Nouvelle-Écosse", "Chaleur waterfront"],
    intro:
      "A francophone village on the Chaleur coast celebrating its mining heritage. Bilingual event and coastal-community security.",
    sectors: ["Bilingual Events", "Coastal", "Heritage Sites"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "nigadoo",
    name: "Nigadoo",
    region: "Chaleur",
    population: "929",
    coords: "47.7500° N, 65.7833° W",
    landmarks: ["Nigadoo River", "Bay of Chaleur coast"],
    intro:
      "A quiet francophone community on the Bay of Chaleur. Residential patrol and small-business protection with bilingual officers.",
    sectors: ["Residential", "Small Business", "Bilingual Services"],
    hero: "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "balmoral",
    name: "Balmoral",
    region: "Restigouche",
    population: "1,706",
    coords: "47.9167° N, 66.4500° W",
    landmarks: ["Balmoral Grist Mill Historic Site", "Restigouche Valley"],
    intro:
      "A rural Restigouche community with a historic 19th-century grist mill. Heritage site security, agricultural protection and rural patrol.",
    sectors: ["Heritage Sites", "Agriculture", "Rural Patrol"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "eel-river-crossing",
    name: "Eel River Crossing",
    region: "Restigouche",
    population: "1,304",
    coords: "47.9833° N, 66.4833° W",
    landmarks: ["Eel River sandbar", "Chaleur coast beaches"],
    intro:
      "A coastal Restigouche village on one of NB's largest natural sandbars. Beach event and cottage security are our focus.",
    sectors: ["Beach Tourism", "Cottages", "Rural Patrol"],
    hero: "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "hanwell",
    name: "Hanwell",
    region: "Capital Region",
    population: "5,262",
    coords: "45.8956° N, 66.7361° W",
    landmarks: ["Hanwell Road commercial corridor", "Route 2 Trans-Canada"],
    intro:
      "A fast-growing rural community immediately west of Fredericton. Residential estate protection, commuter-corridor retail and construction security.",
    sectors: ["Residential Growth", "Retail", "Construction Sites"],
    hero: "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "new-maryland",
    name: "New Maryland",
    region: "Capital Region",
    population: "4,232",
    coords: "45.8792° N, 66.6564° W",
    landmarks: ["New Maryland Centre", "Fredericton commuter belt"],
    intro:
      "A prosperous residential village south of Fredericton. Estate protection, family security and community event coverage.",
    sectors: ["Residential Estates", "Family Events", "Retail"],
    hero: "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const SEO_KEYWORDS = [
  "security guard services New Brunswick",
  "security company New Brunswick",
  "licensed security guards NB",
  "hire security guard Fredericton",
  "security services Saint John",
  "Moncton security company",
  "mobile patrol New Brunswick",
  "event security NB",
  "construction site security Fredericton",
  "loss prevention Moncton",
  "concierge security Saint John",
  "alarm response NB",
  "fire watch New Brunswick",
  "executive protection NB",
  "CCTV monitoring Fredericton",
  "24 hour security guards New Brunswick",
  "bilingual security guards Moncton",
  "private security company NB",
  "commercial security Saint John",
  "retail security Moncton",
];

export const MANIFESTO = [
  {
    number: "01",
    title: "The Doctrine",
    body: "Protection is not a product; it is a discipline. Every officer we deploy across New Brunswick is trained, tested, and re-tested against the same measurable standard. Discipline is what turns a uniform into a deterrent.",
  },
  {
    number: "02",
    title: "Perimeter Control",
    body: "The first hostile act is the crossing of a line. We map every client's perimeter — physical, digital, behavioural — and we hold it. Access is a privilege, granted deliberately, revoked instantly.",
  },
  {
    number: "03",
    title: "Tactical Response",
    body: "When protocol becomes crisis, seconds decide outcomes. Our response protocols are drilled monthly and audited quarterly. We do not improvise under pressure — we execute.",
  },
  {
    number: "04",
    title: "Absolute Discretion",
    body: "The most effective protection is the presence you never noticed. Confidentiality is contractual, cultural, and non-negotiable. Your operations, your people, your reputation — protected in silence.",
  },
];

export const FAQ = [
  {
    q: "Are you licensed to operate across all of New Brunswick?",
    a: "Yes. New Brunswick Security Services holds a provincial Security Services Business license under the Private Investigators and Security Services Act, and every officer holds an individual Class 2 Security Worker license. We operate legally in every incorporated community we serve across New Brunswick.",
  },
  {
    q: "How quickly can you deploy?",
    a: "Standard deployment is within 4 hours anywhere in New Brunswick, and as little as 90 minutes in Fredericton, Saint John and Moncton. Emergency alarm response averages 12 minutes urban, 25 minutes rural.",
  },
  {
    q: "Do you offer bilingual (English / French) officers?",
    a: "Absolutely. Approximately 40% of our roster is fully bilingual. We staff Acadian Peninsula, Madawaska, Dieppe and Caraquet contracts with francophone officers by default.",
  },
  {
    q: "Are your guards armed?",
    a: "No. New Brunswick regulation restricts firearms in security work to specific armoured-car and high-risk applications. Our uniformed officers carry radios, batons where certified, and de-escalation training. VIP details are handled separately and case-by-case.",
  },
  {
    q: "What insurance do you carry?",
    a: "We maintain $5,000,000 in Commercial General Liability, $5,000,000 in Professional Errors & Omissions, and full WorkSafeNB coverage for every officer. Certificates provided on request.",
  },
  {
    q: "Can you scale for large events?",
    a: "Yes. Our largest recent deployment involved 84 officers across a three-day festival. We maintain a 400-person part-time roster for event surges across Atlantic Canada.",
  },
];
